// src/components/events/EventGalleryView.jsx
import React, { useState, useEffect } from 'react';
import { Camera, ZoomIn, Play, Download, Heart } from 'lucide-react';
import { eventsService } from '../../services/eventsService';

export const EventGalleryView = ({ bookId }) => {
  const [galleries, setGalleries] = useState([]);
  const [selectedGallery, setSelectedGallery] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadGalleries();
  }, [bookId]);

  const loadGalleries = async () => {
    try {
      const galleriesData = await eventsService.getEventGalleries(bookId);
      setGalleries(galleriesData);
    } catch (error) {
      console.error('Error loading galleries:', error);
    } finally {
      setLoading(false);
    }
  };

  const allMediaItems = galleries.flatMap(gallery => 
    gallery.mediaItems?.map(item => ({ ...item, galleryName: gallery.name })) || []
  );

  const filteredItems = selectedGallery === 'all' 
    ? allMediaItems 
    : galleries.find(g => g.id === selectedGallery)?.mediaItems || [];

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Camera className="h-8 w-8 text-emerald-600" />
        </div>
        <h2 className="text-3xl font-bold mb-4">Event Gallery</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Browse photos and videos from our event. Capture and share your favorite memories!
        </p>
      </div>

      {/* Gallery Selector */}
      {galleries.length > 1 && (
        <div className="flex justify-center mb-8">
          <div className="bg-gray-100 rounded-lg p-1 inline-flex">
            <button
              onClick={() => setSelectedGallery('all')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                selectedGallery === 'all'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              All Photos ({allMediaItems.length})
            </button>
            {galleries.map(gallery => (
              <button
                key={gallery.id}
                onClick={() => setSelectedGallery(gallery.id)}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  selectedGallery === gallery.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {gallery.name} ({gallery.mediaItems?.length || 0})
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Media Grid */}
      {filteredItems.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map((item, index) => (
            <div key={`${item.id || index}`} className="relative group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <div className="aspect-w-16 aspect-h-12">
                {item.type === 'video' ? (
                  <div className="relative">
                    <img
                      src={item.thumbnail || item.url}
                      alt={item.title || `Gallery item ${index + 1}`}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                        <Play className="h-6 w-6 text-white ml-1" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <img
                    src={item.url}
                    alt={item.title || `Gallery item ${index + 1}`}
                    className="w-full h-48 object-cover"
                  />
                )}
              </div>
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                <div className="flex gap-2">
                  <button className="w-10 h-10 bg-white bg-opacity-90 rounded-full flex items-center justify-center hover:bg-opacity-100 transition-colors">
                    <ZoomIn className="h-5 w-5 text-gray-700" />
                  </button>
                  <button className="w-10 h-10 bg-white bg-opacity-90 rounded-full flex items-center justify-center hover:bg-opacity-100 transition-colors">
                    <Heart className="h-5 w-5 text-gray-700" />
                  </button>
                  <button className="w-10 h-10 bg-white bg-opacity-90 rounded-full flex items-center justify-center hover:bg-opacity-100 transition-colors">
                    <Download className="h-5 w-5 text-gray-700" />
                  </button>
                </div>
              </div>

              {/* Caption */}
              {(item.title || item.caption) && (
                <div className="p-3">
                  <h4 className="font-medium text-gray-900 text-sm">{item.title}</h4>
                  {item.caption && (
                    <p className="text-sm text-gray-600 mt-1">{item.caption}</p>
                  )}
                  {item.galleryName && galleries.length > 1 && (
                    <p className="text-xs text-gray-500 mt-1">{item.galleryName}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-sm font-medium text-gray-900 mb-2">No photos yet</h3>
          <p className="text-sm text-gray-500">Gallery items will appear here as they're added.</p>
        </div>
      )}
    </div>
  );
};